import React, {
  createContext,
  useContext,
  useReducer,
  useState,
  useEffect,
} from "react";
import { useAuth0 } from "../auth/react-auth0-spa";
import { useAgentApi } from "../services/AgentApi";
import appConfig from "../config/appConfig";
import { getAllAgentRoles, userFieldNames } from "../constants/model";
import { getAgentEntitlement } from "../config/agentEntitlementConfig";
import {
  Toaster,
  toastType,
  createNewToast,
  toastReducer,
} from "../components/common/Toaster";
import appRoutes from "../components/Navigation/appRoutes";
import { httpStatusCodes } from "../constants/common";
import useAsyncError from "../utils/asyncError";

const ApplicationContext = createContext();

export const AppContextProvider = (props) => {
  const setAsyncError = useAsyncError();
  const { getMyInfo } = useAgentApi();
  const { isAuthenticated, user } = useAuth0();
  const [agentInfo, setAgentInfo] = useState(null);
  const [agentEntitlement, setAgentEntitlement] = useState(null);
  const [toasts, dispatchToasts] = useReducer(toastReducer, []);
  const [userResultSet, setUserResultSet] = useState({
    users: [],
    pageSize: 15,
    currentPage: 0,
    lastPage: true,
  });
  const [userFilters, setUserFilters] = useState({
    searchBy: userFieldNames.email,
    searchValue: "",
  });

  const history = props.history;
  const errorRoute = history?.location?.pathname === appRoutes.error;

  const isAValidRole = (role) => {
    const matchingRole = getAllAgentRoles().find(
      (agentRole) => agentRole.value === role
    );
    return matchingRole ? true : false;
  };

  useEffect(() => {
    const initAgentInfo = async () => {
      try {
        setAgentInfo(null);

        /*****************************************************************
        TODO: Refactor In Future
        Avoid the need for the errorRoute check.  This is needed currently
        as the setAsyncError(error) triggers a browser redirect (which
        would otherwise loop calls getMyInfo() due the the AppContext
        being outside the react router.
        *****************************************************************/
        if (isAuthenticated && user && !errorRoute) {
          const result = await getMyInfo();

          if (!result?.success) {
            if (result?.statusCode === httpStatusCodes.forbidden_403) {
              history.push(appRoutes.unauthorised);
              return;
            }
            const msg =
              result?.description && result?.description.length > 0
                ? result.description.trim()
                : "Operation failed: unable to determine operator role";
            throw new Error(msg);
          }

          if (!isAValidRole(result?.data?.role)) {
            throw new Error("Operation failed: unnexpected operator role");
          }
          setAgentInfo(result.data);
          return;
        }
      } catch (error) {
        setAsyncError(error);
      }
    };

    initAgentInfo();
  }, [isAuthenticated, user]); //eslint-disable-line

  useEffect(() => {
    const entitlement = getAgentEntitlement(agentInfo?.role);
    setAgentEntitlement(entitlement);
  }, [agentInfo]);

  const toastSuccess = (
    message,
    autoDismissMs = appConfig.toastSuccessDismissMs
  ) => {
    addToast(createNewToast(message, toastType.success), autoDismissMs);
  };

  const toastWarning = (
    message,
    autoDismissMs = appConfig.toastWarningDismissMs
  ) => {
    addToast(createNewToast(message, toastType.warning), autoDismissMs);
  };

  const toastError = (
    message,
    autoDismissMs = appConfig.toastErrorDismissMs
  ) => {
    addToast(createNewToast(message, toastType.error), autoDismissMs);
  };

  const addToast = (toast, autoDismissMs) => {
    dispatchToasts({
      type: "ADD_TOAST",
      toast: toast,
    });
    if (autoDismissMs && autoDismissMs > 0) {
      setTimeout(() => {
        onDismissToast(toast);
      }, autoDismissMs);
    }
  };

  const onDismissToast = (toast) => {
    dispatchToasts({
      type: "REMOVE_TOAST",
      toast: toast,
    });
  };

  const value = {
    agentInfo: props.agentInfo || agentInfo,
    agentEntitlement: props.agentEntitlement || agentEntitlement,
    toastSuccess: props.toastSuccess || toastSuccess,
    toastWarning: props.toastWarning || toastWarning,
    toastError: props.toastError || toastError,
    userResultSet: props.userResultSet || userResultSet,
    setUserResultSet: props.setUserResultSet || setUserResultSet,
    userFilters: props.userFilters || userFilters,
    setUserFilters: props.setUserFilters || setUserFilters,
  };

  return (
    <ApplicationContext.Provider value={value}>
      <Toaster toasts={toasts} onDismissToast={onDismissToast} />
      {props.children}
    </ApplicationContext.Provider>
  );
};

export const useAppContext = () => {
  return useContext(ApplicationContext);
};
